/* Upload size limit, and the wording the client sees when a file is over it.
 *
 * MUST stay in step with MAX_DOC_BYTES in api/app/routers/clients.py — the
 * server is the authority and rejects anything over it; this copy exists only
 * so the portal can say so before spending the client's upload time.
 *
 * There are really two server-side caps, and the client can hit either:
 *
 *   25 MB  the API's own check, which answers with a JSON detail message
 *   26 MB  nginx's client_max_body_size, deliberately 1 MB higher so multipart
 *          overhead doesn't trip it before the API's friendlier check
 *
 * Past 26 MB nginx returns an HTML 413 that never reaches the API, so there is
 * no `detail` to show — which is why `portalFetch` special-cases that status
 * with the same message used here.
 */

export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;
export const MAX_UPLOAD_LABEL = '25 MB';

export const TOO_LARGE_MESSAGE =
  `That file is too large. The limit is ${MAX_UPLOAD_LABEL} per file.`;

function sizeLabel(bytes) {
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Whether a File is over the limit, and what to tell the client if so.
 * Returns null when the file is fine, otherwise a ready-to-show message
 * naming the actual size — "too large" alone leaves people guessing how far
 * over they are and whether re-exporting would help.
 */
export function tooLargeMessage(file) {
  if (!file || typeof file.size !== 'number' || file.size <= MAX_UPLOAD_BYTES) return null;
  const actual = sizeLabel(file.size);
  // A file a few bytes over rounds to "25.0 MB", and "That file is 25.0 MB,
  // the limit is 25 MB" reads as a contradiction. Say "just over" instead.
  const size = actual === MAX_UPLOAD_LABEL.replace(' MB', '.0 MB')
    ? `just over the ${MAX_UPLOAD_LABEL} limit`
    : `${actual}, and the limit is ${MAX_UPLOAD_LABEL} per file`;
  return `That file is ${size}. Please upload a smaller file, or split it into parts.`;
}
